import React, { useState, useMemo } from "react";
import { Button, Form, OverlayTrigger, Tooltip, Badge } from "react-bootstrap";
import { Eye, Printer } from "lucide-react";
import moment from "moment";
import Spinner1 from "../common/spinners";
import { truncateText } from "../../utils/utilities";
import ExcuseDutyModal from "../modals/ExcuseDutyModal";

const ExcuseDutyTable = ({ data, loading, onPrint }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [show, setShow] = useState(false);
  const itemsPerPage = 5;

  const openDetails = (item) => {
    setSelected(item);
    setShow(true);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return data || [];
    const term = searchTerm.toLowerCase();
    return (data || []).filter((item) => {
      const comment = (item.comment || "").toLowerCase();
      const diag = (item.diagnosis || []).map((d) => `${d.diagnosis?.description || ""} ${d.diagnosis?.code || ""}`.toLowerCase()).join(" ");
      const status = (item.status || "").toLowerCase();
      const consultant = (item.createdBy || "").toLowerCase();
      return comment.includes(term) || diag.includes(term) || status.includes(term) || consultant.includes(term);
    });
  }, [data, searchTerm]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getStatusVariant = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "accepted") return "success";
    if (s === "pending") return "warning";
    if (s === "rejected") return "danger";
    return "secondary";
  };

  return (
    <div className="w-100">
      <div className="divider divider-start fw-medium mx-3">
        <span>Excuse Duty History</span>
      </div>

      <Form.Control
        type="text"
        placeholder="Search by note, diagnosis, status..."
        className="my-3 mx-4"
        value={searchTerm}
        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        style={{ width: "280px" }}
      />

      {loading ? (
        <div className="w-100 d-flex justify-content-center">
          <Spinner1 />
        </div>
      ) : !filteredData.length ? (
        <div className="w-100 d-flex justify-content-center text-center pb-4">No Result Found!</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: "700px" }} className="align-middle w-100">
            <thead className="px-3">
              <tr className="px-3">
                <th style={{ paddingLeft: "20px" }}>Date & Period</th>
                <th>Diagnosis</th>
                <th>Consultant</th>
                <th>Status</th>
                <th className="text-end" style={{ paddingRight: "20px" }}>Action</th>
              </tr>
            </thead>
            <tbody className="px-5">
              {currentItems.map((item, index) => {
                const isAccepted = (item.status || "").toLowerCase() === "accepted";
                const diagnosisText = item.diagnosis?.length
                  ? truncateText(item.diagnosis.map((d) => d.diagnosis?.description).join(", "), 40)
                  : "No diagnosis";
                return (
                  <tr key={item.id || index} className="py-3 border-bottom" style={{ height: "65px" }}>
                    <td className="py-3 px-3">
                      <div className="d-flex align-items-center gap-2">
                        <div className="avatar border border-2 border-success text-info d-flex justify-content-center align-items-center">
                          <i className="ri-file-paper-line"></i>
                        </div>
                        <div className="mb-0">
                          <h6 className="d-block text-primary fs-sm mb-1 text-capitalize">
                            {moment(item.createdAt).format("DD MMM, YYYY")} <span className="text-muted fw-normal">({item.sick_days_used != null ? `${item.sick_days_used} day${item.sick_days_used === 1 ? "" : "s"}` : "--"})</span>
                          </h6>
                          <label className="fs-xs">
                            {item.start_date ? moment(item.start_date).format("DD MMM YYYY") : "--"} — {item.end_date ? moment(item.end_date).format("DD MMM YYYY") : "--"}
                          </label>
                          {item.comment && <div className="fs-xs text-muted">{truncateText(item.comment, 35)}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="small text-capitalize">{diagnosisText}</div>
                      <span className="fs-xs text-success">{item.diagnosis?.length ? `${item.diagnosis.length} diagnosis` : ""}</span>
                    </td>
                    <td className="py-3">
                      <div className="small">{item.staff ? `${item.staff.last_name || ""} ${item.staff.first_name || ""}`.trim() : item.createdBy || "--"}</div>
                      <span className="fs-xs text-primary">{moment(item.createdAt).format("h:mm A")}</span>
                    </td>
                    <td className="py-3 text-capitalize">
                      <Badge bg={getStatusVariant(item.status)} className="text-capitalize">{item.status || "Pending"}</Badge>
                    </td>
                    <td className="text-end" style={{ paddingRight: "20px" }}>
                      <OverlayTrigger placement="top" overlay={<Tooltip>View details</Tooltip>}>
                        <Button variant="light" size="sm" className="me-1" onClick={() => openDetails(item)}>
                          <Eye size={16} />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger placement="top" overlay={<Tooltip>{isAccepted ? "Print certificate" : "Only accepted can be printed"}</Tooltip>}>
                        <span className="d-inline-block">
                          <Button
                            variant={isAccepted ? "outline-primary" : "light"}
                            size="sm"
                            disabled={!isAccepted}
                            onClick={() => openDetails(item)}
                            style={!isAccepted ? { opacity: 0.45 } : {}}
                          >
                            <Printer size={16} />
                          </Button>
                        </span>
                      </OverlayTrigger>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="d-flex justify-content-between align-items-center mt-3 mx-3">
            <Button variant="light" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
              Previous
            </Button>
            <span>Page {currentPage} of {totalPages}</span>
            <Button variant="light" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <ExcuseDutyModal show={show} onClose={() => setShow(false)} data={selected} onPrint={onPrint} />
    </div>
  );
};

export default ExcuseDutyTable;
